import { type Environment, NAME_MAX, SECTION_SUGGESTIONS, slugify } from '@fleequid-secrets/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { api, messageOf } from '@/api/client';
import { useOutside } from '@/lib/hooks';
import { Link, navigate } from '@/lib/router';
import { vaultPath } from '@/lib/routes';
import { useShell } from '@/lib/shell';
import { Icon } from './Icon';

// Names worth offering for a new section here: the ones other environments use, then the usual ones.
export function sectionSuggestions(environment: Environment, environments: readonly Environment[]): string[] {
    const here = new Set(environment.sections.map((section) => section.slug));
    const names = [...environments.flatMap((other) => other.sections.map((section) => section.name)), ...SECTION_SUGGESTIONS];

    return [...new Map(names.map((name) => [slugify(name), name])).entries()]
        .filter(([slug]) => !here.has(slug))
        .map(([, name]) => name)
        .slice(0, 6);
}

// A new section, here or in every environment at once. Empty, it needs no confirmation even in production.
export function NewSection(props: { environment: Environment; onClose?: () => void; compact?: boolean }): ReactNode {
    const { environments, say } = useShell();
    const queryClient = useQueryClient();
    const [name, setName] = useState('');
    const [everywhere, setEverywhere] = useState(false);
    const [busy, setBusy] = useState(false);
    const suggestions = useMemo(() => sectionSuggestions(props.environment, environments), [props.environment, environments]);
    const create = (value: string): void => {
        if (!value.trim()) {
            return;
        }
        setBusy(true);
        api.createSection(props.environment.id, { name: value.trim(), everywhere })
            .then(async ({ section }) => {
                await queryClient.invalidateQueries({ queryKey: ['environments'] });
                props.onClose?.();
                navigate(vaultPath(props.environment.slug, section.slug));
            })
            .catch((error: unknown) => say(messageOf(error)))
            .finally(() => setBusy(false));
    };

    return (
        <form
            className={props.compact ? 'rail-new' : 'rail-new big'}
            onSubmit={(event) => {
                event.preventDefault();
                create(name);
            }}
            onKeyDown={(event) => event.key === 'Escape' && props.onClose?.()}
        >
            <input
                className="field"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Backend, Frontend, Cron…"
                maxLength={NAME_MAX}
                autoFocus
            />
            {suggestions.length > 0 && (
                <div className="suggest">
                    {suggestions.map((suggestion) => (
                        <button key={suggestion} type="button" onClick={() => create(suggestion)} disabled={busy}>
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}
            {environments.length > 1 && (
                <button type="button" className={`toggle ${everywhere ? 'on' : ''}`} onClick={() => setEverywhere(!everywhere)}>
                    <span className="sw" />
                    In every environment
                </button>
            )}
            <div className="row">
                {props.onClose && (
                    <button type="button" className="act small" onClick={props.onClose}>
                        Cancel
                    </button>
                )}
                <button className="act small primary" disabled={busy || !name.trim()}>
                    {busy ? 'Creating…' : 'Create'}
                </button>
            </div>
        </form>
    );
}

// An environment's sections, the one on screen lit; a new one from the plus.
export function SectionRail(props: { environment: Environment; activeSlug: string | null }): ReactNode {
    const [adding, setAdding] = useState(false);
    const root = useOutside<HTMLDivElement>(adding, () => setAdding(false));
    const total = props.environment.sections.reduce((sum, section) => sum + section.variableCount, 0);

    return (
        <nav className="rail">
            <div className="rail-head">
                <span className="micro">
                    Sections · <b>{total}</b> variables
                </span>
                <button className="iconbtn" title="New section" onClick={() => setAdding(!adding)}>
                    <Icon name="plus" size={16} />
                </button>
            </div>
            <div ref={root}>{adding && <NewSection environment={props.environment} onClose={() => setAdding(false)} compact />}</div>
            <div className="rail-list">
                {props.environment.sections.map((section) => (
                    <Link
                        key={section.id}
                        to={vaultPath(props.environment.slug, section.slug)}
                        className={`ritem ${section.slug === props.activeSlug ? 'on' : ''}`}
                        title={section.description || section.name}
                    >
                        <span className="name">{section.name}</span>
                        <span className="count">{section.variableCount}</span>
                    </Link>
                ))}
            </div>
            <div className="rail-foot">
                {props.environment.protected && (
                    <span className="lockline">
                        <Icon name="lock" size={14} />
                        {props.environment.name} is protected: changes ask first.
                    </span>
                )}
                <span>Values are encrypted, revealed for a minute, and every look is in the activity.</span>
            </div>
        </nav>
    );
}
